import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronDown, Gamepad2 } from "lucide-react";

interface SlotPickerProps {
  order: string[];
  labels: Record<string, string>;
  pools: Record<string, string[]>;
  isEnabled: (key: string) => boolean;
  isValueOn: (key: string, value: string) => boolean;
  activeCount: (key: string) => number;
  activeAxisCount: number;
  /** Word for the active-count summary, e.g. "axes". */
  unit?: string;
  /** Real game examples for a value, shown in a hover tooltip. */
  valueExamples?: (key: string, value: string) => string[];
  onToggleAxis: (key: string) => void;
  onToggleValue: (key: string, value: string) => void;
  onSetAxisValues: (key: string, on: boolean) => void;
  onEnableAll?: () => void;
  onDisableAll?: () => void;
  onReset: () => void;
}

interface Tip {
  value: string;
  examples: string[];
  x: number;
  y: number;
}

/**
 * Generic, presentational picker: enable/disable each axis, and within each axis
 * pick exactly which values may be used. Reused by the Design Challenge (facets)
 * and the Game-Jam generator (jam slots).
 */
export default function SlotPicker({
  order,
  labels,
  pools,
  isEnabled,
  isValueOn,
  activeCount,
  activeAxisCount,
  unit = "axes",
  valueExamples,
  onToggleAxis,
  onToggleValue,
  onSetAxisValues,
  onEnableAll,
  onDisableAll,
  onReset,
}: SlotPickerProps) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [tip, setTip] = useState<Tip | null>(null);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm text-muted">
          {activeAxisCount} of {order.length} {unit} active
        </span>
        <div className="flex gap-1 text-xs">
          {onEnableAll && (
            <button onClick={onEnableAll} className="rounded-md px-2 py-1 text-muted hover:text-text">
              Enable all
            </button>
          )}
          {onDisableAll && (
            <button onClick={onDisableAll} className="rounded-md px-2 py-1 text-muted hover:text-text">
              Disable all
            </button>
          )}
          <button onClick={onReset} className="rounded-md px-2 py-1 text-muted hover:text-text">
            Reset all
          </button>
        </div>
      </div>

      {order.map((key) => {
        const enabled = isEnabled(key);
        const total = pools[key].length;
        const count = activeCount(key);
        const isOpen = expanded === key;
        return (
          <div key={key} className="overflow-hidden rounded-xl border border-border bg-surface-2/50">
            <div className="flex items-center gap-2 px-3 py-2.5">
              <button
                onClick={() => onToggleAxis(key)}
                aria-label={`Toggle ${labels[key]}`}
                className={[
                  "grid h-5 w-5 shrink-0 place-items-center rounded border transition-colors",
                  enabled ? "border-primary bg-primary text-primary-fg" : "border-border",
                ].join(" ")}
              >
                {enabled && <Check className="h-3.5 w-3.5" />}
              </button>

              <button
                onClick={() => setExpanded(isOpen ? null : key)}
                className="flex flex-1 items-center justify-between gap-2 text-left"
              >
                <span className="font-medium text-text">{labels[key]}</span>
                <span className="flex items-center gap-2">
                  <span className="text-xs text-muted">{enabled ? `${count}/${total}` : "off"}</span>
                  <ChevronDown className={`h-4 w-4 text-muted transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </span>
              </button>
            </div>

            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  className="overflow-hidden border-t border-border"
                >
                  <div className="p-3">
                    <div className="mb-2 flex gap-2 text-xs">
                      <button onClick={() => onSetAxisValues(key, true)} className="rounded-md px-2 py-1 text-muted hover:text-text">
                        All
                      </button>
                      <button onClick={() => onSetAxisValues(key, false)} className="rounded-md px-2 py-1 text-muted hover:text-text">
                        None
                      </button>
                    </div>
                    <div className={`flex flex-wrap gap-2 ${enabled ? "" : "opacity-40"}`}>
                      {pools[key].map((value) => {
                        const on = isValueOn(key, value);
                        const examples = valueExamples?.(key, value) ?? [];
                        return (
                          <button
                            key={value}
                            onClick={() => onToggleValue(key, value)}
                            onMouseEnter={(e) => {
                              if (!examples.length) return;
                              const r = e.currentTarget.getBoundingClientRect();
                              setTip({ value, examples, x: r.left + r.width / 2, y: r.top });
                            }}
                            onMouseLeave={() => setTip(null)}
                            className={[
                              "rounded-lg border px-2.5 py-1 text-xs transition-colors",
                              on
                                ? "border-primary/60 bg-primary/10 text-text"
                                : "border-border bg-surface text-muted hover:text-text",
                            ].join(" ")}
                          >
                            {value}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}

      {/* Fixed tooltip (escapes the panels' overflow clipping). */}
      {tip && (
        <div
          style={{ position: "fixed", left: tip.x, top: tip.y - 8, transform: "translate(-50%, -100%)", zIndex: 60 }}
          className="pointer-events-none w-56 rounded-xl border border-border bg-surface p-3 text-left shadow-2xl"
        >
          <div className="font-display text-sm font-semibold text-text">{tip.value}</div>
          <div className="mt-1.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-primary">
            <Gamepad2 className="h-3 w-3" /> Real examples
          </div>
          <div className="mt-0.5 text-xs text-text/90">{tip.examples.join(" · ")}</div>
        </div>
      )}
    </div>
  );
}
